import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [siteId, setSiteIdState] = useState(() => localStorage.getItem("brigata_site") || "");
  const [meta, setMeta] = useState(null);

  const applyUser = useCallback((u) => {
    setUser(u);
    const sites = u?.sites || [];
    const stored = localStorage.getItem("brigata_site");
    const valid = sites.find((s) => s.id === stored) ? stored : sites[0]?.id || "";
    localStorage.setItem("brigata_site", valid);
    setSiteIdState(valid);
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => applyUser(r.data)).catch(() => setUser(false));
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, [applyUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyUser(data.user);
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    return data.user;
  }, [applyUser]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
  }, []);

  const setSiteId = useCallback((id) => {
    localStorage.setItem("brigata_site", id);
    setSiteIdState(id);
  }, []);

  const value = useMemo(() => ({
    user, siteId, setSiteId, login, logout, meta,
    sites: user?.sites || [],
    site: (user?.sites || []).find((s) => s.id === siteId) || null,
    can: (perm) => !!user?.permissions?.includes(perm),
    reason: (perm) => meta?.permission_reasons?.[perm] || "Operazione non permessa per il tuo ruolo",
    refreshUser: () => api.get("/auth/me").then((r) => applyUser(r.data)),
  }), [user, siteId, setSiteId, login, logout, meta, applyUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
